import { InjectableService } from '@/shared/services/InjectableService'
import { AxiosResponse } from 'axios'
import { submitApiRequest, getTenantPath, HttpMethod } from '@/services/aplosApi'
import type { AuditLogQueryParams } from '../models/AuditLogQueryParams'
import type { AuditLogQueryResponse } from '../models/AuditLogQueryResponse'
import type { AuditConfigStatusResponse } from '../models/AuditConfigStatus'

const DEFAULT_LIMIT = 25

export interface IAuditLogApiService {
  queryByTenant(tenantId: string, params?: AuditLogQueryParams): Promise<AxiosResponse<AuditLogQueryResponse>>
  queryByUser(tenantId: string, userId: string, params?: AuditLogQueryParams): Promise<AxiosResponse<AuditLogQueryResponse>>
  queryByResource(tenantId: string, resourceType: string, resourceId: string, params?: AuditLogQueryParams): Promise<AxiosResponse<AuditLogQueryResponse>>
  getAuditConfigStatus(tenantId: string): Promise<AxiosResponse<AuditConfigStatusResponse>>
}

/**
 * Builds a query string from AuditLogQueryParams.
 * Defaults limit to 25 when not specified.
 * Returns a string starting with '?' or empty string if no params.
 */
export function buildAuditLogQueryString(params?: AuditLogQueryParams): string {
  const parts: string[] = []
  const limit = params?.limit ?? DEFAULT_LIMIT
  parts.push(`limit=${limit}`)
  if (params?.start_key) parts.push(`start_key=${encodeURIComponent(params.start_key)}`)
  if (params?.action) parts.push(`action=${encodeURIComponent(params.action)}`)
  if (params?.category) parts.push(`category=${encodeURIComponent(params.category)}`)
  if (params?.start_time) parts.push(`start_time=${encodeURIComponent(params.start_time)}`)
  if (params?.end_time) parts.push(`end_time=${encodeURIComponent(params.end_time)}`)
  if (params?.resource_type) parts.push(`resource_type=${encodeURIComponent(params.resource_type)}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

export class AuditLogApiService extends InjectableService implements IAuditLogApiService {

  static readonly injectableKey = 'IAuditLogApiService'

  get injectableKey() {
    return (this.constructor as typeof AuditLogApiService).injectableKey
  }

  async queryByTenant(tenantId: string, params?: AuditLogQueryParams): Promise<AxiosResponse<AuditLogQueryResponse>> {
    const tenantPath = getTenantPath(tenantId)
    const qs = buildAuditLogQueryString(params)
    return await submitApiRequest(HttpMethod.GET, `${tenantPath}/audit-logs${qs}`)
  }

  async queryByUser(tenantId: string, userId: string, params?: AuditLogQueryParams): Promise<AxiosResponse<AuditLogQueryResponse>> {
    const tenantPath = getTenantPath(tenantId)
    const qs = buildAuditLogQueryString(params)
    return await submitApiRequest(HttpMethod.GET, `${tenantPath}/audit-logs/users/${userId}${qs}`)
  }

  async queryByResource(tenantId: string, resourceType: string, resourceId: string, params?: AuditLogQueryParams): Promise<AxiosResponse<AuditLogQueryResponse>> {
    const tenantPath = getTenantPath(tenantId)
    const qs = buildAuditLogQueryString(params)
    return await submitApiRequest(HttpMethod.GET, `${tenantPath}/audit-logs/resources/${resourceType}/${resourceId}${qs}`)
  }

  async getAuditConfigStatus(tenantId: string): Promise<AxiosResponse<AuditConfigStatusResponse>> {
    const tenantPath = getTenantPath(tenantId)
    return await submitApiRequest(HttpMethod.GET, `${tenantPath}/audit-config/status`)
  }
}
